import {ProductDetailsSection_ArticleDetail_SizeLane} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ContentPopper,
  DropDownProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  FlyoutProps,
  ModalDialogHeaderContent,
  ModalDialogProps,
  SecondaryContentPopperButton,
  Text,
  Z_INDEXES,
  mobileOnly,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC, useState} from 'react';
import sc, {css} from 'styled-components';
import {BodyMeasurementsChart} from './BodyMeasurementsChart';
import {MeasurementHints} from './MeasurementHints';
import {SpecificSizeChart} from './SpecificSizeChart';
import {useSizeChartsOpenedState} from './useSizeChartsOpenedState';

export interface SizeChartComponentProps {
  SizeChartLayout: React.FC<FlyoutProps> | React.FC<ModalDialogProps>;
  SizeSystemLayout: React.FC<FlyoutProps> | React.FC<DropDownProps>;
}

export const SizeCharts: FC<
  SizeChartComponentProps &
    ProductDetailsSection_ArticleDetail_SizeLane['sizeCharts']
> = ({
  SizeChartLayout,
  SizeSystemLayout,
  bodyMeasurementsTable,
  measurementHints,
  productSizeTable,
  introductionText,
}) => {
  const {t} = useTranslation();
  const {isOpen, cancel} = useSizeChartsOpenedState();
  const [selectedSizeSystemIndex, setSelectedSizeSystemIndex] =
    useState<number>(0);

  return (
    <StyledContentPopper
      LayoutComponent={SizeChartLayout}
      onClose={cancel}
      open={isOpen}
      data-testid="sizeChartsSection"
      renderInPortal
      headerContent={
        <Header data-testid="sizeChartTitle">{t('Size Chart')}</Header>
      }
      bodyContent={
        <BodyWrapper>
          <IntroText data-testid="sizeChartIntroText">
            {introductionText}
          </IntroText>
          <Section>
            <SpecificSizeChart
              selectedOption={selectedSizeSystemIndex}
              productSizeTable={productSizeTable}
              setSelectedSizeSystemType={setSelectedSizeSystemIndex}
              sizeSystemLayout={SizeSystemLayout}
            />
          </Section>
          {bodyMeasurementsTable && (
            <Section>
              <BodyMeasurementsChart
                selectedOption={selectedSizeSystemIndex}
                bodyMeasurementsTable={bodyMeasurementsTable}
                setSelectedSizeSystemType={setSelectedSizeSystemIndex}
                sizeSystemLayout={SizeSystemLayout}
              />
            </Section>
          )}
          <Section>
            <MeasurementHints measurementHints={measurementHints} />
          </Section>
        </BodyWrapper>
      }
      footerContent={
        <SecondaryContentPopperButton label={t('Close')} onClick={cancel} />
      }
    />
  );
};

const Header = sc.span`
  ${mobileOnly(css`
    font-size: ${FONT_SIZES.px20};
    font-weight: ${FONT_WEIGHTS.w500};
  `)}
`;

const BodyWrapper = sc.div`
  overflow: auto;
`;

const Section = sc.div`
  margin-bottom: 40px;

  &:first-of-type {
    margin-top: 40px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const IntroText = sc(Text)`
  line-height: 1.43;
`;

const StyledContentPopper = linariaStyled(ContentPopper)`
  #modalContent {
    padding-top: 0;
    width: 600px;
  }

  ${ModalDialogHeaderContent} {
    background-color: ${COLORS.White};
    position: sticky;
    top: 0;
    z-index: ${Z_INDEXES.SIZE_CHART_MODAL_HEADER};
  }
`;
